// src/components/publishing/brand-publisher-page.tsx
import React, { useEffect, useState, useCallback } from "react";
import {
  ArrowLeft, Sparkles, ClipboardPaste, Rocket, Trash2, Shield,
  PanelLeft, X,
} from "lucide-react";
import { ModuleHeader } from "../shell/module-header";
import { toast } from "sonner";
import {
  publishingApi, Draft, Integration, Destination, PerDestinationContent,
  PublishResponse,
} from "@/app/services/publishing/publishing-api";
import { ConfirmDialog } from "@/app/components/shared/confirm-dialog";
import { useResponsive } from "../../hooks/common/use-breakpoint";
import { Button, Icon, IconButton, Spinner } from "../kit";
import {
  DestinationDot, Orb, destinationLabel as destLabel,
} from "@/app/components/shared/advanced-vocabulary";
import "@/styles/advanced-tier.css";
import "@/styles/publisher-page.css";

import { AvailableServersStrip } from "./available-servers-strip";
import { DraftComposer } from "./draft-composer";
import { createBrief } from "@/api/content";
import { PasteContentComposer } from "./paste-content-composer";
import { DraftPreviewTabs } from "./draft-preview-tabs";
import { PublishConfirmCard, PublishResultsCard } from "./publish-confirm-card";

interface BrandPublisherPageProps {
  /** Currently active brand. Publishing is scoped per-brand. */
  activeBrandName?: string;
  /**
   * Parent router. Called with a page name (e.g. "integrations") when the
   * user clicks a deep-link CTA like "Go to Integrations" or the "Manage"
   * button in the Available Servers strip.
   */
  onNavigate?: (page: string) => void;
}

type ComposerMode = "ai" | "paste";

export function BrandPublisherPage({
  activeBrandName, onNavigate,
}: BrandPublisherPageProps) {
  // Internal alias — all the existing logic below uses `brandName`.
  const brandName = activeBrandName;
  // Deep-link to Integrations page.
  const onNavigateToIntegrations = onNavigate
    ? () => onNavigate("integrations")
    : undefined;

  const { isDesktop } = useResponsive();

  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(true);

  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [loadingDrafts, setLoadingDrafts] = useState(true);

  const [activeDraftId, setActiveDraftId] = useState<number | null>(null);
  const [activeDraft, setActiveDraft] = useState<Draft | null>(null);

  const [composerMode, setComposerMode] = useState<ComposerMode>("ai");
  const [composerBusy, setComposerBusy] = useState(false);

  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [lastPublishResult, setLastPublishResult] = useState<PublishResponse | null>(null);

  const [draftToDelete, setDraftToDelete] = useState<Draft | null>(null);
  const [rail, setRail] = useState(false); // off-canvas drawer on mobile

  const refreshIntegrations = useCallback(async () => {
    setLoadingIntegrations(true);
    try {
      const list = await publishingApi.listIntegrations(brandName);
      setIntegrations(list);
    } catch (e: any) {
      toast.error(e.message || "Could not load integrations");
    } finally {
      setLoadingIntegrations(false);
    }
  }, [brandName]);

  const refreshDrafts = useCallback(async () => {
    setLoadingDrafts(true);
    try {
      const list = await publishingApi.listDrafts(brandName);
      setDrafts(list);
    } catch (e: any) {
      toast.error(e.message || "Could not load drafts");
    } finally {
      setLoadingDrafts(false);
    }
  }, [brandName]);

  useEffect(() => {
    refreshIntegrations();
    refreshDrafts();
    setActiveDraftId(null);
    setActiveDraft(null);
    setLastPublishResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandName]);

  useEffect(() => {
    if (activeDraftId === null) {
      setActiveDraft(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const d = await publishingApi.getDraft(activeDraftId);
        if (!cancelled) setActiveDraft(d);
      } catch (e: any) {
        if (!cancelled) toast.error(e.message || "Could not load draft");
      }
    })();
    return () => { cancelled = true; };
  }, [activeDraftId]);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleGenerateDraft = async (params: {
    brief: string;
    destinations: Destination[];
    tone?: string;
    actionId?: number | null;
    customerId?: number | null;
  }) => {
    setComposerBusy(true);
    try {
      // #132: an action's brief from the brand record; without one the
      // server makes a free-text brief when the brand has a record.
      const brief = params.actionId != null && params.customerId != null
        ? await createBrief(params.customerId, { action_id: params.actionId })
        : null;
      const created = await publishingApi.createDraft({
        brand_name: brandName,
        brief: params.brief,
        destinations: params.destinations,
        tone: params.tone,
        brief_id: brief?.id,
      });
      toast.success("Draft ready");
      await refreshDrafts();
      setActiveDraftId(created.id);
      setLastPublishResult(null);
    } catch (e: any) {
      toast.error(e.message || "Drafting failed");
    } finally {
      setComposerBusy(false);
    }
  };

  const handleCreateManualDraft = async (params: {
    destinations: Destination[];
    per_destination_content: PerDestinationContent;
    title_hint?: string;
  }) => {
    setComposerBusy(true);
    try {
      const created = await publishingApi.createManualDraft({
        brand_name: brandName,
        destinations: params.destinations,
        per_destination_content: params.per_destination_content,
        title_hint: params.title_hint,
      });
      toast.success("Draft saved");
      await refreshDrafts();
      setActiveDraftId(created.id);
      setLastPublishResult(null);
    } catch (e: any) {
      toast.error(e.message || "Could not save draft");
    } finally {
      setComposerBusy(false);
    }
  };

  const handleDeleteDraft = async () => {
    const d = draftToDelete;
    if (!d) return;
    try {
      await publishingApi.deleteDraft(d.id);
      toast.success("Draft deleted");
      if (activeDraftId === d.id) setActiveDraftId(null);
      await refreshDrafts();
    } catch (e: any) {
      toast.error(e.message || "Delete failed");
    } finally {
      setDraftToDelete(null);
    }
  };

  const handlePublished = (result: PublishResponse) => {
    setLastPublishResult(result);
    setPublishConfirmOpen(false);
    // Refresh draft to pick up status changes
    if (activeDraftId) {
      publishingApi.getDraft(activeDraftId).then(d => setActiveDraft(d)).catch(() => {});
    }
    refreshDrafts();
  };

  // ─── Derived ───────────────────────────────────────────────────────────────

  const hasAnyConnected = integrations.some(i => i.status === "active");

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div data-kit className="upm-pub">
      {/* Left rail — drafts list */}
      {isDesktop ? (
        <LeftRail
          drafts={drafts}
          loading={loadingDrafts}
          activeDraftId={activeDraftId}
          onSelect={(id) => { setActiveDraftId(id); setLastPublishResult(null); }}
          onDelete={setDraftToDelete}
          onNew={() => { setActiveDraftId(null); setLastPublishResult(null); }}
        />
      ) : (
        rail && (
          <MobileDrawer onClose={() => setRail(false)}>
            <LeftRail
              drafts={drafts}
              loading={loadingDrafts}
              activeDraftId={activeDraftId}
              onSelect={(id) => { setActiveDraftId(id); setLastPublishResult(null); setRail(false); }}
              onDelete={(d) => { setDraftToDelete(d); setRail(false); }}
              onNew={() => { setActiveDraftId(null); setLastPublishResult(null); setRail(false); }}
            />
          </MobileDrawer>
        )
      )}

      {/* Main column */}
      <div className="upm-pub__main">
        <PageHeader
          brandName={brandName}
          isDesktop={isDesktop}
          onOpenRail={() => setRail(true)}
        />

        <div className="upm-pub__body">
          {/* Available servers strip */}
          {/* The whole list, not just the active ones: a destination that is
              NOT connected is the state that gates publishing, and it has to
              be legible before you hit the gate. */}
          <AvailableServersStrip
            integrations={integrations}
            loading={loadingIntegrations}
            onManageClick={onNavigateToIntegrations}
          />

          {/* Composer OR Draft preview */}
          {activeDraft === null ? (
            <ComposerCard>
              <ModeToggle mode={composerMode} onChange={setComposerMode} disabled={composerBusy} />

              {composerMode === "ai" && (
                <DraftComposer
                  integrations={integrations}
                  brandName={brandName}
                  onDraft={handleGenerateDraft}
                  drafting={composerBusy}
                  onNavigateToIntegrations={onNavigateToIntegrations}
                />
              )}

              {composerMode === "paste" && (
                <PasteContentComposer
                  integrations={integrations}
                  brandName={brandName}
                  onCreate={handleCreateManualDraft}
                  creating={composerBusy}
                />
              )}
            </ComposerCard>
          ) : (
            <>
              <DraftHeader
                draft={activeDraft}
                onBack={() => { setActiveDraftId(null); setLastPublishResult(null); }}
                onDelete={() => setDraftToDelete(activeDraft)}
              />

              {/* A ReviewCard, not the studio. Nothing is being authored
                  here — it is being read — and wearing the authoring surface
                  would say otherwise. */}
              <ReviewCard>
                <DraftPreviewTabs
                  draft={activeDraft}
                  onUpdated={setActiveDraft}
                />
              </ReviewCard>

              {lastPublishResult ? (
                <PublishResultsCard
                  result={lastPublishResult}
                  onDismiss={() => setLastPublishResult(null)}
                />
              ) : publishConfirmOpen ? (
                <PublishConfirmCard
                  draft={activeDraft}
                  integrations={integrations}
                  onCancel={() => setPublishConfirmOpen(false)}
                  onPublished={handlePublished}
                />
              ) : (
                /* The one page-level action here, so the one solid teal
                   control on the screen. It was green with WHITE ink, which
                   is 2.0:1. */
                <div className="upm-pub__publishbar">
                  <span className="upm-pub__publishmark">
                    <Icon icon={Shield} size="sm" />
                  </span>
                  <div className="upm-pub__publishcopy">
                    <div className="upm-pub__publishtitle">Ready when you are</div>
                    <div className="upm-pub__publishsub">
                      {hasAnyConnected
                        ? "Review each destination tab, then publish. You can ship them individually."
                        : "Connect at least one destination before publishing."}
                    </div>
                  </div>
                  <Button
                    icon={Rocket}
                    onClick={() => setPublishConfirmOpen(true)}
                    disabled={!hasAnyConnected}
                    title={!hasAnyConnected ? "Connect at least one destination first" : undefined}
                  >
                    Review and publish
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={draftToDelete !== null}
        onOpenChange={(open) => { if (!open) setDraftToDelete(null); }}
        onConfirm={handleDeleteDraft}
        title={draftToDelete ? "Delete this draft?" : "Delete?"}
        description="This removes the draft permanently. Any posts already published will stay published."
        confirmLabel="Delete"
        destructive
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Sub-components
// ═══════════════════════════════════════════════════════════════════════════

function PageHeader({
  brandName, isDesktop, onOpenRail,
}: {
  brandName?: string;
  isDesktop: boolean;
  onOpenRail: () => void;
}) {
  return (
    <ModuleHeader
      page="Brand Publisher"
      icon={Rocket}
      compact
      collapsible
      title="Push updates to your channels. Watch models re-index."
      description={`Close the loop from insight to published action, then confirm the models picked it up.${brandName ? ` Publishing for ${brandName}.` : ""}`}
      actionsSlot={
        !isDesktop ? (
          <IconButton icon={PanelLeft} label="Open drafts" onClick={onOpenRail} />
        ) : undefined
      }
    />
  );
}

function ComposerCard({ children }: { children: React.ReactNode }) {
  /* The luminous studio. The same surface the report studio wears, because
     these are the two places in the product where the model AUTHORS something
     and a reader who has met one should recognise the other. */
  return (
    <section className="upm-pub__studio">
      <div className="upm-pub__aura" aria-hidden="true" />
      <div className="upm-pub__studioinner">
        <div className="upm-pub__studiohead">
          <Orb size="md" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="upm-adv-eyebrow upm-adv-eyebrow--reason">Publisher</div>
            <h2 className="upm-pub__studiotitle">What should we put out there?</h2>
            <p className="upm-pub__studiosub">
              I will write it for each destination in its own voice. You approve every one before it ships.
            </p>
          </div>
        </div>
        {children}
      </div>
    </section>
  );
}

/** A plain panel, for the draft review. Not the studio: nothing is being
    authored here, it is being read. */
function ReviewCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="upm-pub__doc">
      <div className="upm-pub__docrule" />
      <div className="upm-pub__docinner">{children}</div>
    </div>
  );
}

function ModeToggle({
  mode, onChange, disabled,
}: {
  mode: ComposerMode;
  onChange: (m: ComposerMode) => void;
  disabled: boolean;
}) {
  /* The toggle sits with the FIELD it governs — below the headline, above the
     brief — not beside the headline, where it strangled the H2 to four lines. */
  return (
    <div className="upm-pub__modes" role="group" aria-label="How to create this draft">
      <button
        type="button"
        className="upm-pub__mode"
        aria-pressed={mode === "ai"}
        disabled={disabled}
        onClick={() => onChange("ai")}
      >
        <Icon icon={Sparkles} size="sm" />
        Generate with AI
      </button>
      <button
        type="button"
        className="upm-pub__mode"
        aria-pressed={mode === "paste"}
        disabled={disabled}
        onClick={() => onChange("paste")}
      >
        <Icon icon={ClipboardPaste} size="sm" />
        Paste existing content
      </button>
    </div>
  );
}

// ─── Drafts rail ─────────────────────────────────────────────────────────────

function LeftRail({
  drafts, loading, activeDraftId, onSelect, onDelete, onNew,
}: {
  drafts: Draft[];
  loading: boolean;
  activeDraftId: number | null;
  onSelect: (id: number) => void;
  onDelete: (d: Draft) => void;
  onNew: () => void;
}) {
  return (
    <div className="upm-pub__rail">
      <div className="upm-pub__railhead">
        <div className="upm-pub__railtop">
          <span className="upm-adv-eyebrow">Drafts</span>
          <span className="upm-pub__count">{drafts.length}</span>
        </div>
        <Button variant="secondary" icon={Sparkles} onClick={onNew}>
          New draft
        </Button>
      </div>

      <div className="upm-pub__raillist">
        {loading && drafts.length === 0 && <Spinner label="Loading drafts" />}

        {!loading && drafts.length === 0 && (
          <p className="upm-pub__pickmeta" style={{ padding: "var(--sp-5) var(--sp-3)" }}>
            No drafts yet. Generate or paste content to get started.
          </p>
        )}

        {drafts.map(d => (
          <DraftListItem
            key={d.id}
            draft={d}
            active={d.id === activeDraftId}
            onClick={() => onSelect(d.id)}
            onDelete={() => onDelete(d)}
          />
        ))}
      </div>
    </div>
  );
}

/** "2h ago" from a timestamp. An unparseable date renders nothing rather than
    "Invalid Date" — the reading is absent, so the element is. */
function ago(iso?: string | null): string {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (!Number.isFinite(t)) return "";
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function DraftListItem({
  draft, active, onClick, onDelete,
}: {
  draft: Draft;
  active: boolean;
  onClick: () => void;
  onDelete: () => void;
}) {
  /* ONE primary identifier — the brief — with everything else demoted to the
     meta line under it. The row used to lead with an uppercase status word, so
     five drafts read as five statuses and the thing you were looking for came
     third. */
  return (
    <div
      className="upm-pub__draft"
      role="button"
      tabIndex={0}
      aria-current={active}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        onClick();
      }}
    >
      <div className="upm-pub__draftbrief">
        {draft.brief || <span className="upm-approve__absent">No brief</span>}
      </div>
      <div className="upm-pub__draftmeta">
        <StatusChip status={draft.status} />
        {/* Dots, not labelled pills: three destination names on a 280px row is
            most of the row, and the dot plus its title attribute says the same
            thing in a tenth of the space. */}
        {draft.destinations.map(d => (
          <span key={d} title={destLabel(d)}>
            <DestinationDot id={d} />
          </span>
        ))}
        <span className="upm-pub__draftago">{ago(draft.created_at)}</span>
        <IconButton
          icon={Trash2}
          label={`Delete draft: ${draft.brief || "untitled"}`}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
        />
      </div>
    </div>
  );
}

const STATUS_TONE: Record<string, string> = {
  published: "upm-pub-status--published",
  partially_published: "upm-pub-status--partial",
  failed: "upm-pub-status--failed",
  approved: "upm-pub-status--approved",
};

function StatusChip({ status }: { status: string }) {
  return (
    <span className={`upm-pub-status ${STATUS_TONE[status] ?? ""}`}>
      {statusLabel(status)}
    </span>
  );
}

function DraftHeader({
  draft, onBack, onDelete,
}: {
  draft: Draft;
  onBack: () => void;
  onDelete: () => void;
}) {
  const isManual = !draft.model_used;
  return (
    <div className="upm-pub__draftbar">
      <Button variant="secondary" icon={ArrowLeft} onClick={onBack}>
        New draft
      </Button>
      <span className={isManual ? "upm-pub__chip" : "upm-pub__chip upm-pub__chip--ai"}>
        <Icon icon={isManual ? ClipboardPaste : Sparkles} size="sm" />
        {isManual ? "Pasted" : "AI-drafted"}
      </span>
      <StatusChip status={draft.status} />
      <span className="upm-pub__docmeta">
        Written for {draft.destinations.length}
        {draft.destinations.length === 1 ? " destination" : " destinations"}
      </span>
      <span className="upm-pub__draftbar-end">
        <Button variant="secondary" icon={Trash2} onClick={onDelete}>
          Delete
        </Button>
      </span>
    </div>
  );
}

function MobileDrawer({
  children, onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <>
      <div className="upm-pub__scrim" onClick={onClose} />
      <div className="upm-pub__drawer">
        <span className="upm-pub__drawerclose">
          <IconButton icon={X} label="Close drafts" onClick={onClose} />
        </span>
        {children}
      </div>
    </>
  );
}

function statusLabel(s: string): string {
  return {
    pending: "Draft",
    approved: "Approved",
    published: "Published",
    partially_published: "Partial",
    failed: "Failed",
    cancelled: "Cancelled",
  }[s] || s;
}